import {
  Alert,
  Badge,
  Button,
  Drawer,
  Group,
  NumberInput,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useCreateInitialBalancesBatch } from 'src/Hooks/Mutations/useCreateInitialBalancesBatch';
import useFetchMembers from 'src/Hooks/Queries/useFetchMembers';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { trackAccountsClosed } from 'src/Services/Mixpanel/events';
import { formatClosingDate } from 'src/Utils/formatClosingDate';

import styles from './InitialBalancesDrawer.module.scss';
import { Props } from './InitialBalancesDrawer.types';

interface MemberBalanceFormValue {
  amount: number;
  label: string;
}

interface FormValues {
  asOfDate: string;
  balances: Record<string, MemberBalanceFormValue>;
}

// The last 4 half-year pivot dates (01/01 or 01/07), most recent first — a closed list,
// per the product decision that initial balances only ever land on a semester boundary.
const getPivotDateOptions = (locale: string): { value: string; label: string }[] => {
  const now = new Date();
  let year = now.getFullYear();
  let half: 1 | 2 = now.getMonth() < 6 ? 1 : 2;

  return Array.from({ length: 4 }, () => {
    const value = half === 1 ? `${year}-01-01` : `${year}-07-01`;
    const option = { value, label: formatClosingDate(value, locale) };
    if (half === 1) {
      half = 2;
      year -= 1;
    } else {
      half = 1;
    }
    return option;
  });
};

const InitialBalancesDrawer = ({ open, onClose }: Props) => {
  const { t, i18n } = useTranslation();
  const assetId = useCurrentAssetId();
  const { data: membersData } = useFetchMembers();
  const { mutate, isLoading } = useCreateInitialBalancesBatch({ onSuccess: onClose });

  const pivotOptions = useMemo(() => getPivotDateOptions(i18n.language), [i18n.language]);
  const members = useMemo(() => membersData?.members ?? [], [membersData]);

  const form = useForm<FormValues>({
    initialValues: { asOfDate: pivotOptions[0]?.value ?? '', balances: {} },
  });

  useEffect(() => {
    if (!open) return;
    form.setFieldValue(
      'balances',
      Object.fromEntries(members.map(member => [member.id, { amount: 0, label: '' }]))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, members]);

  const total = Object.values(form.values.balances).reduce(
    (sum, balance) => sum + (balance.amount ?? 0),
    0
  );

  const closingDateLabel = form.values.asOfDate
    ? formatClosingDate(form.values.asOfDate, i18n.language)
    : '';

  const handleSave = () => {
    if (!assetId) return;
    mutate(
      {
        assetId,
        data: {
          asOfDate: form.values.asOfDate,
          balances: members.map(member => ({
            // `member.id` is a literal 'owner' sentinel for the owner's row (kept for existing UI
            // conventions) — assetMemberId is the real AssetMember foreign key the backend needs.
            assetMemberId: member.assetMemberId ?? member.id,
            amount: form.values.balances[member.id]?.amount ?? 0,
            label: form.values.balances[member.id]?.label || undefined,
          })),
        },
      },
      {
        onSuccess: () =>
          trackAccountsClosed({
            closing_date: form.values.asOfDate,
            members_count: members.length,
          }),
      }
    );
  };

  return (
    <Drawer
      opened={open}
      onClose={onClose}
      position="right"
      size="xl"
      title={t('admin.initialBalances.title', 'Arrêter les comptes')}
    >
      <Stack gap="lg">
        <div>
          <Text size="sm" fw={500} mb="xs">
            {t('admin.initialBalances.closingDate', 'Date de clôture')}
          </Text>
          <SegmentedControl
            fullWidth
            value={form.values.asOfDate}
            onChange={value => form.setFieldValue('asOfDate', value)}
            data={pivotOptions}
          />
        </div>

        <Alert icon={<IconAlertTriangle size={16} />} color="orange" variant="light">
          {t(
            'admin.initialBalances.preClosingWarning',
            'Les opérations antérieures à cette date restent consultables, mais ne comptent plus dans les soldes : elles sont déjà intégrées dans les montants saisis ci-dessous.'
          )}
        </Alert>

        <Table verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t('admin.initialBalances.member', 'Membre')}</Table.Th>
              <Table.Th>
                {t('admin.initialBalances.amountAt', 'Solde au {{date}} (€)', {
                  date: closingDateLabel,
                })}
              </Table.Th>
              <Table.Th>{t('admin.initialBalances.label', 'Libellé (optionnel)')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {members.map(member => (
              <Table.Tr key={member.id}>
                <Table.Td>
                  <Group gap={6} wrap="nowrap">
                    <Text size="sm">
                      {member.user.firstName} {member.user.lastName}
                    </Text>
                    {member.isPlaceholder && (
                      <Badge size="xs" variant="outline" color="gray">
                        {t('admin.placeholderMembers.badge', 'Non-inscrit')}
                      </Badge>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <NumberInput
                    value={form.values.balances[member.id]?.amount ?? 0}
                    onChange={value =>
                      form.setFieldValue(
                        `balances.${member.id}.amount`,
                        typeof value === 'number' ? value : 0
                      )
                    }
                    suffix=" €"
                    decimalScale={2}
                    w={140}
                  />
                </Table.Td>
                <Table.Td>
                  <TextInput
                    value={form.values.balances[member.id]?.label ?? ''}
                    onChange={e =>
                      form.setFieldValue(`balances.${member.id}.label`, e.currentTarget.value)
                    }
                    placeholder={t(
                      'admin.initialBalances.labelPlaceholder',
                      'Ex : reprise du carnet papier'
                    )}
                  />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        {Math.abs(total) > 0.01 && (
          <Alert color="blue" variant="light">
            {t(
              'admin.initialBalances.totalNotZero',
              'Le total des soldes saisis est de {{total}} € (un total non nul est possible).',
              { total: total.toFixed(2) }
            )}
          </Alert>
        )}
      </Stack>

      <div className={styles.footer}>
        <Text fw={600}>
          {t('admin.initialBalances.total', 'Total : {{total}} €', { total: total.toFixed(2) })}
        </Text>
        <Group>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button onClick={handleSave} loading={isLoading} disabled={!members.length}>
            {t('admin.initialBalances.save', 'Arrêter les comptes au {{date}}', {
              date: closingDateLabel,
            })}
          </Button>
        </Group>
      </div>
    </Drawer>
  );
};

export default InitialBalancesDrawer;
